import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

/**
 * Tema do console: `system` segue o aparelho; `light`/`dark` forçam via `data-theme` no
 * <html>. A escolha fica no localStorage e o script do index.html a aplica antes do
 * primeiro paint, então nunca há flash de tema errado.
 */
export type Theme = 'system' | 'light' | 'dark';

const KEY = 'talkium.theme';

interface ThemeApi {
  theme: Theme;
  /** O que está de fato na tela, já resolvido `system` → claro/escuro. */
  resolved: 'light' | 'dark';
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeApi>({
  theme: 'system',
  resolved: 'light',
  setTheme: () => {},
});

function readStored(): Theme {
  try {
    const stored = localStorage.getItem(KEY);
    return stored === 'light' || stored === 'dark' ? stored : 'system';
  } catch {
    return 'system';
  }
}

function systemPrefersDark(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-color-scheme: dark)').matches
    : false;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(readStored);
  const [systemDark, setSystemDark] = useState(systemPrefersDark);

  useEffect(() => {
    if (theme === 'system') document.documentElement.removeAttribute('data-theme');
    else document.documentElement.setAttribute('data-theme', theme);
    try {
      localStorage.setItem(KEY, theme);
    } catch {
      /* armazenamento indisponível: o tema vale só nesta visita */
    }
  }, [theme]);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (event: MediaQueryListEvent) => setSystemDark(event.matches);
    media.addEventListener?.('change', onChange);
    return () => media.removeEventListener?.('change', onChange);
  }, []);

  const api = useMemo<ThemeApi>(
    () => ({
      theme,
      resolved: theme === 'system' ? (systemDark ? 'dark' : 'light') : theme,
      setTheme: setThemeState,
    }),
    [theme, systemDark],
  );

  return <ThemeContext.Provider value={api}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeApi {
  return useContext(ThemeContext);
}
